import React from 'react';
import cn from 'classnames';

export type ActionMenuItem = {
	key: string;
	label: React.ReactNode;
	onSelect: () => void;
	disabled?: boolean;
};

export interface ActionMenuProps {
	items: ActionMenuItem[];
	triggerLabel?: string;
	triggerClassName?: string;
	menuClassName?: string;
	itemClassName?: string;
	disabled?: boolean;
}

function DotsIcon(): React.ReactElement {
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			<path
				d="M4.5 8.5C3.67157 8.5 3 9.17157 3 10C3 10.8284 3.67157 11.5 4.5 11.5C5.32843 11.5 6 10.8284 6 10C6 9.17157 5.32843 8.5 4.5 8.5Z"
				fill="currentColor"
			/>
			<path
				d="M10 8.5C9.17157 8.5 8.5 9.17157 8.5 10C8.5 10.8284 9.17157 11.5 10 11.5C10.8284 11.5 11.5 10.8284 11.5 10C11.5 9.17157 10.8284 8.5 10 8.5Z"
				fill="currentColor"
			/>
			<path
				d="M15.5 8.5C14.6716 8.5 14 9.17157 14 10C14 10.8284 14.6716 11.5 15.5 11.5C16.3284 11.5 17 10.8284 17 10C17 9.17157 16.3284 8.5 15.5 8.5Z"
				fill="currentColor"
			/>
		</svg>
	);
}

export function ActionMenu({
	items,
	triggerLabel,
	triggerClassName,
	menuClassName,
	itemClassName,
	disabled,
}: ActionMenuProps): React.ReactElement | null {
	const [open, setOpen] = React.useState(false);
	const rootRef = React.useRef<HTMLDivElement | null>(null);

	const visibleItems = React.useMemo(() => items.filter(Boolean), [items]);
	const close = React.useCallback(() => setOpen(false), []);

	React.useEffect(() => {
		if (!open) return;

		const handlePointerDown = (event: MouseEvent | PointerEvent) => {
			const root = rootRef.current;
			if (!root) return;
			if (event.target instanceof Node && root.contains(event.target)) return;
			close();
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') close();
		};

		window.addEventListener('pointerdown', handlePointerDown);
		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('pointerdown', handlePointerDown);
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [close, open]);

	if (visibleItems.length === 0) return null;

	return (
		<div ref={rootRef} className="relative inline-flex">
			<button
				type="button"
				className={cn(
					'inline-flex items-center justify-center rounded-full w-10 h-10 text-black hover:bg-black/5 active:bg-black/10',
					triggerClassName,
				)}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				disabled={disabled}
			>
				{triggerLabel ? <span className="sr-only">{triggerLabel}</span> : null}
				<DotsIcon />
			</button>

			{open ? (
				<div
					role="menu"
					className={cn(
						'absolute right-0 top-[calc(100%+8px)] z-50 min-w-[220px] rounded-2xl bg-white shadow-[0_12px_32px_rgba(0,0,0,0.12)] p-2',
						menuClassName,
					)}
				>
					{visibleItems.map((item) => (
						<button
							key={item.key}
							type="button"
							role="menuitem"
							className={cn(
								'w-full text-left px-3 py-2 rounded-xl text-base hover:bg-black/5 active:bg-black/10 disabled:opacity-50 disabled:cursor-not-allowed',
								itemClassName,
							)}
							onClick={() => {
								if (item.disabled) return;
								item.onSelect();
								close();
							}}
							disabled={item.disabled}
						>
							{item.label}
						</button>
					))}
				</div>
			) : null}
		</div>
	);
}

